/*
 * Figma `48:33` — Alert. Tone: Info, Success, Warning, Danger.
 *
 * Each tone gets a glyph as well as a fill, for the same reason the badge gets a dot. Danger and
 * warning announce themselves (`role="alert"`) because they usually appear in response to something
 * the person just did; info and success do not, because an alert role interrupts and a confirmation
 * that interrupts is a nuisance.
 */
import type { ReactNode } from 'react';
import { IconInfo, IconSuccess, IconWarning, IconDanger } from './icons';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const GLYPH: Record<AlertTone, (p: { size?: number }) => ReactNode> = {
  info: IconInfo, success: IconSuccess, warning: IconWarning, danger: IconDanger,
};

export function Alert({ tone = 'info', title, children, action }: {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  const Glyph = GLYPH[tone];
  return (
    <div className="jt-alert" data-tone={tone} role={tone === 'danger' || tone === 'warning' ? 'alert' : undefined}>
      <span className="jt-alert-icon"><Glyph size={18} /></span>
      <div className="jt-alert-body">
        {title && <div className="jt-alert-title">{title}</div>}
        <div className="jt-alert-text">{children}</div>
      </div>
      {action}
    </div>
  );
}
